import { FC, ReactNode } from 'react';
import { css } from '@emotion/react';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import { fontStyles, spacing, colors } from '../../styles';

// Helper function to safely parse simple HTML content with <strong> tags
const parseSimpleHTML = (text: string): ReactNode => {
  const parts = text.split(/(<strong>.*?<\/strong>)/g);
  return parts.map((part, index) => {
    if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
      const content = part.replace(/<\/?strong>/g, '');
      return <strong key={index}>{content}</strong>;
    }
    return part;
  });
};

export interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface IndicatorCardProps {
  emoji: string;
  title: string;
  description: string;
  items: string[];
  color: string;
  backgroundColor: string;
  backgroundColorDark: string;
}

const IndicatorCard: FC<IndicatorCardProps> = ({
  emoji,
  title,
  description,
  items,
  color,
  backgroundColor,
  backgroundColorDark,
}) => (
  <div
    css={css`
      background-color: ${backgroundColor};
      border-radius: 12px;
      padding: ${spacing.regular};
      margin-bottom: ${spacing.regular};
      html[data-theme='dark'] & {
        background-color: ${backgroundColorDark};
      }
    `}
  >
    <h3
      css={css`
        ${fontStyles.lion}
        color: ${color};
        margin: 0 0 ${spacing.deci} 0;
        display: flex;
        align-items: center;
      `}
    >
      {emoji} {title}
    </h3>
    <p
      css={css`
        ${fontStyles.cat}
        color: ${colors.liquorice};
        margin: 0 0 ${spacing.deci} 0;
        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
      `}
    >
      {description}
    </p>
    <ul
      css={css`
        ${fontStyles.cat}
        color: ${colors.liquorice};
        margin: 0;
        padding-left: 20px;
        list-style: disc;
        html[data-theme='dark'] & {
          color: ${colors.sugar};
        }
        li {
          margin-top: ${spacing.deci};
          &::marker {
            color: ${color};
          }
        }
      `}
    >
      {items.map((item) => (
        <li key={item}>{parseSimpleHTML(item)}</li>
      ))}
    </ul>
  </div>
);

const MarketUpdatesBottomSheet: FC<Props> = ({ isOpen, onClose }) => (
  <BottomSheet isOpen={isOpen} onClose={onClose} title="About Market Indicators">
    {/* Market Indicators Section */}
    <div
      css={css`
        padding-top: ${spacing.centi};
      `}
    >
      <h3
        css={css`
          ${fontStyles.lion}
          color: ${colors.liquorice};
          margin: 0 0 ${spacing.deci} 0;
          display: flex;
          align-items: center;
          gap: ${spacing.milli};
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
        aria-label="Your Market Indicators"
      >
        <span aria-hidden="true">📊</span> Your Market Indicators
      </h3>
      <p
        css={css`
          ${fontStyles.cat}
          color: ${colors.liquorice};
          margin: 0 0 ${spacing.regular} 0;
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        Easily track key market signals to decide whether to be aggressive or defensive with your portfolio.
        Historically, the best time to be aggressive is during extreme fear, and the best time to be defensive is during
        extreme greed. However, this can be very difficult because your portfolio will incur losses during extreme fear,
        while FOMO kicks in during extreme greed.
      </p>

      <IndicatorCard
        emoji="🟢"
        title="Bullish Sentiment"
        description="Indicators showing positive market momentum"
        color={colors.leaf}
        backgroundColor="rgba(52, 168, 83, 0.1)"
        backgroundColorDark="rgba(52, 168, 83, 0.15)"
        items={[
          '<strong>AAII:</strong> More than 10% more investors are bullish than bearish in the weekly sentiment survey',
          '<strong>Fear Index:</strong> CNN\'s indicator shows "Greed" or "Extreme Greed" (score above 55)',
          '<strong>Volatility:</strong> VXX shows low or declining volatility, indicating market calm',
          '<strong>Momentum:</strong> Both SPY and QQQ are trading more than 2% above their 20-day exponential moving averages',
        ]}
      />

      <IndicatorCard
        emoji="⚪"
        title="Neutral Sentiment"
        description="Mixed signals or inconclusive readings"
        color={colors.silver}
        backgroundColor="rgba(169, 169, 169, 0.1)"
        backgroundColorDark="rgba(169, 169, 169, 0.15)"
        items={[
          '<strong>AAII:</strong> Bullish and bearish investors are within 10% of each other',
          '<strong>Fear Index:</strong> CNN\'s indicator shows "Neutral" (score between 45-55)',
          '<strong>Volatility:</strong> VXX shows normal volatility levels',
          '<strong>Momentum:</strong> SPY or QQQ is within 2% of its 20-day exponential moving average, or they show conflicting signals',
        ]}
      />

      <IndicatorCard
        emoji="🔴"
        title="Bearish Sentiment"
        description="Indicators showing negative market momentum"
        color={colors.berry}
        backgroundColor="rgba(235, 87, 87, 0.1)"
        backgroundColorDark="rgba(235, 87, 87, 0.15)"
        items={[
          '<strong>AAII:</strong> More than 10% more investors are bearish than bullish in the weekly sentiment survey',
          '<strong>Fear Index:</strong> CNN\'s indicator shows "Fear" or "Extreme Fear" (score below 45)',
          '<strong>Volatility:</strong> VXX shows high or rising volatility, indicating market uncertainty',
          '<strong>Momentum:</strong> Both SPY and QQQ are trading more than 2% below their 20-day exponential moving averages',
        ]}
      />
    </div>

    {/* Data Sources Section */}
    <div
      css={css`
        margin-bottom: ${spacing.regular};
        padding-top: ${spacing.regular};
        border-top: 1px solid ${colors.cloud};
        html[data-theme='dark'] & {
          border-top-color: ${colors.tungsten};
        }
      `}
    >
      <h3
        css={css`
          ${fontStyles.lion}
          color: ${colors.liquorice};
          margin: 0 0 ${spacing.deci} 0;
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        Data Sources
      </h3>
      <ul
        css={css`
          ${fontStyles.cat}
          color: ${colors.liquorice};
          margin: 0;
          padding-left: 20px;
          list-style: disc;
          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
          li {
            margin-top: ${spacing.deci};
          }
          a {
            color: ${colors.petrolBlue};
            text-decoration: none;
            &:hover {
              text-decoration: underline;
            }
          }
        `}
      >
        <li>
          <a href="https://www.aaii.com/sentimentsurvey" target="_blank" rel="noopener noreferrer">
            AAII Sentiment Survey
          </a>
          : Weekly investor sentiment poll
        </li>
        <li>
          <a href="https://edition.cnn.com/markets/fear-and-greed" target="_blank" rel="noopener noreferrer">
            CNN Fear & Greed Index
          </a>
          : Market emotion indicator
        </li>
        <li>
          <a href="https://www.cboe.com/tradable_products/vix/" target="_blank" rel="noopener noreferrer">
            CBOE Volatility Index
          </a>
          : Market volatility via VXX
        </li>
        <li>
          <a href="https://www.google.com/finance/quote/SPY:NYSEARCA" target="_blank" rel="noopener noreferrer">
            SPY
          </a>
          {' & '}
          <a href="https://www.google.com/finance/quote/QQQ:NASDAQ" target="_blank" rel="noopener noreferrer">
            QQQ
          </a>
          : S&P 500 and Nasdaq momentum
        </li>
      </ul>
    </div>

    <Button
      variant="secondary"
      block
      onClick={onClose}
      css={css`
        margin-top: ${spacing.regular};
        ${fontStyles.cat}
      `}
    >
      Close
    </Button>
  </BottomSheet>
);

export default MarketUpdatesBottomSheet;
